import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const { refreshUser } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      if (n > 8) {
        setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshUser();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setAttempts(n);
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        setStatus("error");
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <Card className="border-gray-200 shadow-none rounded-2xl max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === "polling" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-emerald-700 animate-spin mb-3" />
              <h1 className="font-display font-bold text-2xl">Confirming your payment…</h1>
              <p className="text-gray-600 mt-1.5">Please don't close this window. Attempt {attempts + 1}/8.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-700 mb-3" />
              <h1 className="font-display font-bold text-2xl">Welcome to Premium!</h1>
              <p className="text-gray-600 mt-1.5">10% bonus cashback is now active on your account.</p>
              <Button onClick={() => nav("/app")} className="mt-6 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="billing-go-app">
                Go to dashboard
              </Button>
            </>
          )}
          {(status === "timeout" || status === "expired" || status === "error") && (
            <>
              <XCircle className="w-12 h-12 mx-auto text-rose-600 mb-3" />
              <h1 className="font-display font-bold text-2xl">We couldn't confirm</h1>
              <p className="text-gray-600 mt-1.5">If you completed payment, your premium will activate within minutes.</p>
              <Button onClick={() => nav("/app/premium")} className="mt-6 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">
                Try again
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function BillingCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <Card className="border-gray-200 shadow-none rounded-2xl max-w-md w-full">
        <CardContent className="p-8 text-center">
          <XCircle className="w-12 h-12 mx-auto text-amber-600 mb-3" />
          <h1 className="font-display font-bold text-2xl">Payment cancelled</h1>
          <p className="text-gray-600 mt-1.5">No worries — you can upgrade any time.</p>
          <Button onClick={() => nav("/app/premium")} className="mt-6 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">
            Back to Premium
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
